import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {CaminhoEntrada, CategoriaWizard, ProdutoWizard} from '../../wizard-state.model';
import {ProdutoService} from '../../../../services/produto.service';
import {ImportadorIfoodService} from '../../../../services/importador-ifood.service';

@Component({
  selector: 'app-step-produtos',
  templateUrl: './step-produtos.component.html',
  styleUrls: ['./step-produtos.component.scss']
})
export class StepProdutosComponent implements OnInit {
  @Input() caminhoEscolhido: CaminhoEntrada | null = null;
  @Input() produtosImportados: ProdutoWizard[] = [];
  @Input() produtosTemplate: ProdutoWizard[] = [];
  @Input() produtosManuais: ProdutoWizard[] = [];
  @Input() categoriasImportadas: CategoriaWizard[] = [];
  @Input() empresa: any = {};
  @Output() onConcluido = new EventEmitter<void>();
  @Output() onVoltar = new EventEmitter<void>();

  produtoManual = {nome: '', descricao: '', preco: null as number};
  previewLimitePorCategoria = 2;
  previewLimiteRemovidos = 6;
  salvandoManual = false;
  salvandoSelecao = false;
  erroManual: string | null = null;
  erroValidacao: string | null = null;
  erroImportacao: string | null = null;
  categoriasExpandidas = new Set<string>();
  removidosExpandidos = false;
  totalSalvar = 0;
  processadosSalvar = 0;
  produtoImportandoAtual: string | null = null;
  limparCatalogoExistente = false;
  totalProdutosCatalogoAtual = 0;
  carregandoResumoCatalogo = false;
  faseImportacao: 'limpando' | 'importando' = 'importando';

  constructor(
    private produtoService: ProdutoService,
    private importadorService: ImportadorIfoodService
  ) {}

  async ngOnInit() {
    if (this.caminhoEscolhido === 'importar' && this.empresa?.catalogo?.id) {
      await this.carregueResumoCatalogoAtual();
    }
  }

  get produtos(): ProdutoWizard[] {
    if (this.caminhoEscolhido === 'importar') return this.produtosImportados;
    if (this.caminhoEscolhido === 'template') return this.produtosTemplate;
    return this.produtosManuais;
  }

  get produtosSelecionados(): ProdutoWizard[] {
    return this.produtos.filter(p => p.selecionado);
  }

  get categorias(): string[] {
    const cats = new Set(this.produtosSelecionados.map(p => p.categoria));
    return Array.from(cats);
  }

  produtosPorCategoria(categoria: string): ProdutoWizard[] {
    return this.produtosSelecionados.filter(p => p.categoria === categoria);
  }

  get produtosRemovidos(): ProdutoWizard[] {
    return this.produtos.filter(p => !p.selecionado);
  }

  get totalCategoriasSelecionadas(): number {
    return this.categorias.length;
  }

  produtosVisiveisPorCategoria(categoria: string): ProdutoWizard[] {
    const produtos = this.produtosPorCategoria(categoria);
    return this.categoriasExpandidas.has(categoria)
      ? produtos
      : produtos.slice(0, this.previewLimitePorCategoria);
  }

  categoriaTemMaisProdutos(categoria: string): boolean {
    return this.produtosPorCategoria(categoria).length > this.previewLimitePorCategoria;
  }

  categoriaExpandida(categoria: string): boolean {
    return this.categoriasExpandidas.has(categoria);
  }

  toggleCategoria(categoria: string) {
    if (this.categoriasExpandidas.has(categoria)) {
      this.categoriasExpandidas.delete(categoria);
    } else {
      this.categoriasExpandidas.add(categoria);
    }
  }

  get produtosRemovidosVisiveis(): ProdutoWizard[] {
    return this.removidosExpandidos
      ? this.produtosRemovidos
      : this.produtosRemovidos.slice(0, this.previewLimiteRemovidos);
  }

  get haMaisRemovidos(): boolean {
    return this.produtosRemovidos.length > this.previewLimiteRemovidos;
  }

  get progressoImportacaoPercentual(): number {
    if (!this.totalSalvar) return 0;
    return Math.round((this.processadosSalvar / this.totalSalvar) * 100);
  }

  get podeLimparCatalogoExistente(): boolean {
    return this.caminhoEscolhido === 'importar' && this.totalProdutosCatalogoAtual > 0;
  }

  get ehModoManual(): boolean {
    return this.caminhoEscolhido === 'zero' || this.caminhoEscolhido === 'planilha';
  }

  get ehModoTemplate(): boolean {
    return this.caminhoEscolhido === 'template';
  }

  toggleProduto(produto: ProdutoWizard) {
    produto.selecionado = !produto.selecionado;
    this.erroImportacao = null;
  }

  removerTodosExemplos() {
    this.produtosTemplate.forEach(p => p.selecionado = false);
  }

  selecionarTodosExemplos() {
    this.produtosTemplate.forEach(p => p.selecionado = true);
  }

  adicionarProdutoManual() {
    if (!this.produtoManual.nome || !this.produtoManual.preco) return;

    this.salvandoManual = true;
    this.erroManual = null;

    const produto: any = {
      nome: this.produtoManual.nome,
      descricao: this.produtoManual.descricao,
      preco: this.produtoManual.preco,
      catalogo: this.empresa.catalogo,
      temEstoque: true
    };

    this.produtoService.salveProduto(produto).then(resp => {
      this.produtosManuais.push({
        id: resp?.id || null,
        nome: this.produtoManual.nome,
        descricao: this.produtoManual.descricao,
        preco: this.produtoManual.preco,
        categoria: 'Meus Produtos',
        selecionado: true,
        salvo: !!resp?.id
      });
      this.produtoManual = {nome: '', descricao: '', preco: null};
      this.salvandoManual = false;
    }).catch(err => {
      this.salvandoManual = false;
      this.erroManual = err || 'Erro ao salvar produto.';
    });
  }

  removerProdutoManual(index: number) {
    this.produtosManuais.splice(index, 1);
  }

  podeAvancar(): boolean {
    if (this.ehModoManual) return this.produtosManuais.length > 0;
    return this.produtosSelecionados.length > 0;
  }

  async avancar() {
    if (!this.podeAvancar()) {
      this.erroValidacao = this.ehModoManual
        ? 'Adicione ao menos um produto antes de continuar.'
        : 'Selecione ao menos um produto antes de continuar.';
      return;
    }

    if (!this.ehModoManual) {
      const salvou = await this.salveProdutosSelecionados();
      if (!salvou) return;
    }

    this.erroValidacao = null;
    this.onConcluido.emit();
  }

  voltar() {
    this.onVoltar.emit();
  }

  private async salveProdutosSelecionados(): Promise<boolean> {
    const produtosParaSalvar = this.produtosSelecionados.filter(produto => !produto.salvo);
    const totalProdutosRemover = this.limparCatalogoExistente ? this.totalProdutosCatalogoAtual : 0;

    if (!produtosParaSalvar.length && !totalProdutosRemover) {
      this.erroImportacao = null;
      return true;
    }

    this.salvandoSelecao = true;
    this.erroImportacao = null;
    this.totalSalvar = produtosParaSalvar.length + totalProdutosRemover;
    this.processadosSalvar = 0;
    this.produtoImportandoAtual = totalProdutosRemover
      ? 'Limpando cardápio atual'
      : (produtosParaSalvar[0]?.nome || null);

    try {
      if (this.limparCatalogoExistente && this.totalProdutosCatalogoAtual > 0) {
        this.faseImportacao = 'limpando';
        await this.limpeCatalogoAtual();
      }

      this.faseImportacao = 'importando';
      for (const produto of produtosParaSalvar) {
        this.produtoImportandoAtual = produto.nome;
        const payload = this.montePayloadProduto(produto);
        const produtoSalvo = await this.importadorService.importeProduto(payload);

        produto.id = produtoSalvo?.id || produto.id || null;
        produto.salvo = !!produto.id;
        produto.payload = {...payload, ...produtoSalvo};
        this.processadosSalvar++;
      }

      if (this.caminhoEscolhido === 'importar') {
        await this.importadorService.finalizouImportacao().catch(() => null);
      }

      return true;
    } catch (err: any) {
      this.erroImportacao = err || 'Não foi possível salvar os produtos selecionados.';
      return false;
    } finally {
      this.salvandoSelecao = false;
      this.produtoImportandoAtual = null;
      this.faseImportacao = 'importando';
    }
  }

  private async carregueResumoCatalogoAtual(): Promise<void> {
    this.carregandoResumoCatalogo = true;

    try {
      const resposta = await this.produtoService.listeTodos(this.empresa.catalogo.id, 0, 1, false);
      this.totalProdutosCatalogoAtual = resposta?.total || 0;
      if (!this.totalProdutosCatalogoAtual) {
        this.limparCatalogoExistente = false;
      }
    } catch {
      this.totalProdutosCatalogoAtual = 0;
      this.limparCatalogoExistente = false;
    } finally {
      this.carregandoResumoCatalogo = false;
    }
  }

  private async limpeCatalogoAtual(): Promise<void> {
    const produtosExistentes = await this.carregueProdutosExistentesCatalogo();

    if (!produtosExistentes.length) {
      this.totalProdutosCatalogoAtual = 0;
      this.limparCatalogoExistente = false;
      return;
    }

    const tamanhoLote = 100;
    for (let i = 0; i < produtosExistentes.length; i += tamanhoLote) {
      const lote = produtosExistentes.slice(i, i + tamanhoLote);
      await this.produtoService.removaProdutoCatalogo(lote);
      this.processadosSalvar += lote.length;
    }

    this.totalProdutosCatalogoAtual = 0;
    this.limparCatalogoExistente = false;
  }

  private async carregueProdutosExistentesCatalogo(): Promise<any[]> {
    const catalogoId = this.empresa?.catalogo?.id;
    if (!catalogoId) return [];

    const totalPorPagina = 100;
    const produtos: any[] = [];
    let inicio = 0;
    let total = 0;

    do {
      const resposta = await this.produtoService.listeTodos(catalogoId, inicio, totalPorPagina, false);
      const produtosPagina = resposta?.produtos || [];
      total = resposta?.total || 0;

      if (!produtosPagina.length) break;

      produtos.push(...produtosPagina);
      inicio += totalPorPagina;
    } while (produtos.length < total);

    return produtos;
  }

  private montePayloadProduto(produto: ProdutoWizard): any {
    const payload = produto.payload ? {...produto.payload} : {};

    payload.nome = payload.nome || produto.nome;
    payload.descricao = payload.descricao != null ? payload.descricao : (produto.descricao || '');
    payload.preco = payload.preco != null ? payload.preco : produto.preco;
    payload.categoria = payload.categoria || (produto.categoria ? {nome: produto.categoria} : null);
    payload.categoriasAcima = Array.isArray(payload.categoriasAcima) ? payload.categoriasAcima : [];
    payload.camposAdicionais = Array.isArray(payload.camposAdicionais)
      ? payload.camposAdicionais
      : (produto.adicionais || []);
    payload.disponibilidades = Array.isArray(payload.disponibilidades) ? payload.disponibilidades : [];

    if (!payload.urlImagemExterna && produto.imagem) {
      payload.urlImagemExterna = produto.imagem;
    }

    return payload;
  }
}
